import React, { useEffect, useState } from "react";
import BackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PrintIcon from "@mui/icons-material/LocalPrintshopOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import DeliveryIcon from "@mui/icons-material/LocalShippingOutlined";
import NotesIcon from "@mui/icons-material/NotesOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import BookingIcon from "@mui/icons-material/BookOnlineOutlined";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import MusicIcon from "@mui/icons-material/MusicNoteOutlined";
import GroupIcon from "@mui/icons-material/PeopleOutlined";
import FavoriteIcon from "@mui/icons-material/FavoriteBorderOutlined";
import PdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Chip as UiChip } from "../../components/ui";
import { showErrorToast, showSuccessToast } from "../../components/common/ShowToast";
import WhatsAppMessageDialog from "../../components/common/WhatsAppMessageDialog";
import apiService from "../../service/Api";
import type { RootState } from "../../store/Index";
import { tokens } from "../../theme";
import {
  mapApiProgramOrder,
  type ProgramOrder,
  type ProgramOrderStatus,
} from "./ProgramOrdersPage";

const statusLabels: Record<ProgramOrderStatus, string> = {
  new: "New",
  in_review: "In review",
  proof_sent: "Proof sent",
  approved: "Approved",
  printing: "Printing",
  dispatched: "Ready for delivery",
  completed: "Delivered",
  cancelled: "Cancelled",
};

const statusColors: Record<
  ProgramOrderStatus,
  "neutral" | "info" | "warning" | "success" | "error"
> = {
  new: "info",
  in_review: "warning",
  proof_sent: "info",
  approved: "success",
  printing: "warning",
  dispatched: "info",
  completed: "success",
  cancelled: "neutral",
};

const ALL_STATUS_OPTIONS: { value: ProgramOrderStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In review" },
  { value: "proof_sent", label: "Proof sent" },
  { value: "approved", label: "Approved" },
  { value: "printing", label: "Printing" },
  { value: "dispatched", label: "Ready for delivery" },
  { value: "completed", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_FLOW: ProgramOrderStatus[] = [
  "new",
  "in_review",
  "proof_sent",
  "approved",
  "printing",
  "dispatched",
  "completed",
];

async function copyText(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    showSuccessToast(`${label} copied.`);
  } catch {
    showErrorToast(`Could not copy ${label.toLowerCase()}.`);
  }
}

function buildCanvaText(order: ProgramOrder): string {
  const lines: string[] = [];
  const name = [order.programTitle, order.deceasedName].filter(Boolean).join(" - ");
  lines.push(name || order.deceasedName);
  if (order.programSubtitle) lines.push(order.programSubtitle);
  const dates = [order.birthDate, order.passingDate].filter(Boolean).join("  -  ");
  if (dates) lines.push(dates);
  if (order.venue) lines.push(`Venue: ${order.venue}`);
  if (order.serviceTime) lines.push(`Service time: ${order.serviceTime}`);
  if (order.serviceDate) lines.push(`Service date: ${formatDate(order.serviceDate)}`);

  if (order.obituary) {
    lines.push("", "OBITUARY", order.obituary);
  }

  if (order.sections.length > 0) {
    lines.push("", "ORDER OF SERVICE");
    order.sections.forEach((section, index) => {
      lines.push(`${index + 1}. ${section.title}${section.detail ? ` - ${section.detail}` : ""}`);
    });
  }

  if (order.hymns.length > 0) {
    lines.push("", "HYMNS");
    order.hymns.forEach((hymn) => {
      lines.push(`${hymn.title}${hymn.performer ? ` (${hymn.performer})` : ""}`);
      if (hymn.lyrics) lines.push(hymn.lyrics);
      lines.push("");
    });
  }

  if (order.pallbearers.length > 0) {
    lines.push("PALLBEARERS", order.pallbearers.join(", "));
  }

  if (order.acknowledgement) {
    lines.push("", "ACKNOWLEDGEMENT", order.acknowledgement);
  }

  return lines.join("\n");
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const ProgramOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const brandTextColor = theme.palette.getContrastText(colors.primary[400]);
  const isDark = theme.palette.mode === "dark";

  const orderFromNavigation = (location.state as { order?: ProgramOrder } | null)?.order;
  const [order, setOrder] = useState<ProgramOrder | null>(orderFromNavigation ?? null);
  const [loading, setLoading] = useState(!orderFromNavigation);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    // The list page already fetched this row and passed it via navigation
    // state, so skip the duplicate GET when it's available.
    if (orderFromNavigation) return;
    let active = true;
    const loadOrder = async () => {
      if (!id) return;
      setLoading(true);
      setLoadError("");
      try {
        const response = await apiService.get<Parameters<typeof mapApiProgramOrder>[0]>(
          `/api/bookings/program-orders/${id}/`,
        );
        if (active) setOrder(mapApiProgramOrder(response.data));
      } catch {
        if (active) setLoadError("This program order could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadOrder();
    return () => { active = false; };
  }, [id, orderFromNavigation]);

  const handleStatusUpdate = async (newStatus: ProgramOrderStatus) => {
    if (!order) return;
    try {
      await apiService.patch(`/api/bookings/program-orders/${order.id}/`, { status: newStatus });
      setOrder((prev) => (prev ? { ...prev, status: newStatus } : null));
      showSuccessToast(`Order status changed to ${statusLabels[newStatus]}`);
    } catch {
      showErrorToast("Could not update the order status.");
    }
  };
  const [messageOpen, setMessageOpen] = useState(false);
  const [sendingBusiness, setSendingBusiness] = useState(false);

  if (loading) {
    return <Box sx={{ minHeight: "50vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  if (!order) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/program-orders")}>
          Back to program orders
        </Button>
        <Alert severity="info" sx={{ mt: 2 }}>{loadError || "This program order could not be found."}</Alert>
      </Box>
    );
  }

  const sendWithMuzukuruBusiness = async (message: string) => {
    if (!order) return;
    const recipient = order.phone.replace(/\D/g, "").replace(/^0/, "263");
    if (!recipient) {
      showErrorToast("The applicant does not have a valid phone number.");
      return;
    }

    setSendingBusiness(true);
    try {
      const response = await apiService.post<{
        ok: boolean;
        message?: string;
        error?: string;
      }>("/api/whatsapp/send-direct/", { recipient, message });
      if (response.data?.ok) {
        showSuccessToast(response.data.message || "WhatsApp message sent.");
        setMessageOpen(false);
      } else {
        showErrorToast(response.data?.error || "Could not send the WhatsApp message.");
      }
    } catch (error: unknown) {
      const messageText = axios.isAxiosError(error)
        ? error.response?.data?.error || error.message
        : error instanceof Error
          ? error.message
          : "Could not send the WhatsApp message.";
      showErrorToast(messageText);
    } finally {
      setSendingBusiness(false);
    }
  };

  const emailSubject = `Program order ${order.orderNumber}`;
  const emailBody = `Hi ${order.clientName.split(" ")[0]},\n\nThanks for submitting the memorial program for ${order.deceasedName}. We are reviewing order ${order.orderNumber} and will be in touch about the proof and printing.`;

  const currentStatusIndex = STATUS_FLOW.indexOf(order.status);

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 8 }}>
      {/* Back button — matches ViewBooking2 style */}
      <Button
        onClick={() => navigate("/program-orders")}
        startIcon={<BackIcon sx={{ fontSize: 14 }} />}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 1.1,
          mb: 2,
          borderRadius: "999px",
          bgcolor: colors.primary[800],
          color: colors.primary[400],
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          border: `1px solid ${colors.primary[400]}`,
          "&:hover": { borderColor: `${colors.primary[400]}44` },
        }}
      >
        BACK
      </Button>

      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} mb={0.5}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 18, sm: 22 },
                letterSpacing: "-0.5px",
                color: colors.primary[100],
              }}
            >
              {order.programTitle}
            </Typography>
            <UiChip label={statusLabels[order.status]} status={statusColors[order.status]} />
            
            {/* Quick Status Dropdown */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                size="small"
                value={order.status}
                onChange={(e) => void handleStatusUpdate(e.target.value as ProgramOrderStatus)}
                sx={{
                  height: 30,
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: isDark ? "action.hover" : "background.paper",
                }}
              >
                {ALL_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Advance to next status button */}
            {currentStatusIndex >= 0 && currentStatusIndex < STATUS_FLOW.length - 1 && (
              <Button
                size="small"
                variant="contained"
                onClick={() => void handleStatusUpdate(STATUS_FLOW[currentStatusIndex + 1])}
                sx={{
                  height: 30,
                  bgcolor: colors.primary[400],
                  color: brandTextColor,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  boxShadow: 0,
                  px: 1.5,
                  "&:hover": { bgcolor: colors.primary[500] },
                }}
              >
                Advance to {statusLabels[STATUS_FLOW[currentStatusIndex + 1]]}
              </Button>
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<BookingIcon />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Convert to booking
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArticleIcon />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Create proposal
          </Button>
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={() => setMessageOpen(true)}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            WhatsApp client
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            component="a"
            href={`mailto:${order.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Email client
          </Button>
        </Stack>
      </Stack>

      {/* Status pipeline */}
      <Paper
        sx={{
          borderRadius: "14px",
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          boxShadow: 0,
          bgcolor: colors.primary[900],
          overflowX: "auto",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.6px",
            color: "text.secondary",
            mb: 2,
            textTransform: "uppercase",
          }}
        >
          Order status pipeline
        </Typography>
        <Stack direction="row" spacing={0} sx={{ minWidth: 560 }}>
          {STATUS_FLOW.map((s, idx) => {
            const isDone = idx < currentStatusIndex;
            const isCurrent = idx === currentStatusIndex;
            return (
              <React.Fragment key={s}>
                <Box onClick={() => void handleStatusUpdate(s)} sx={{ flex: 1, textAlign: "center", cursor: "pointer", "&:hover": { opacity: 0.85 } }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 0.75,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDone
                        ? colors.primary[400]
                        : isCurrent
                          ? alpha(colors.primary[400], 0.13)
                          : isDark
                            ? alpha(theme.palette.common.white, 0.06)
                            : theme.palette.action.hover,
                      border: isCurrent ? `2px solid ${colors.primary[400]}` : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone ? (
                      <CheckIcon sx={{ fontSize: 14, color: brandTextColor }} />
                    ) : isCurrent ? (
                      <PendingIcon sx={{ fontSize: 14, color: colors.primary[400] }} />
                    ) : (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: isDark
                            ? alpha(theme.palette.common.white, 0.2)
                            : theme.palette.grey[300],
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: isCurrent ? 700 : 500,
                      color: isCurrent
                        ? colors.primary[400]
                        : isDone
                          ? isDark ? colors.primary[300] : colors.primary[700]
                          : "text.disabled",
                      lineHeight: 1.3,
                    }}
                  >
                    {statusLabels[s]}
                  </Typography>
                </Box>
                {idx < STATUS_FLOW.length - 1 && (
                  <Box
                    sx={{
                      height: "2px",
                      flex: 0.4,
                      mt: "13px",
                      bgcolor: idx < currentStatusIndex
                        ? colors.primary[400]
                        : isDark
                          ? alpha(theme.palette.common.white, 0.08)
                          : theme.palette.divider,
                      transition: "background-color 0.3s",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Paper>

      {/* Main content grid */}
      <Grid container spacing={2.5}>
        {/* Left: Program details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={2.5}>
            {/* Everything a designer needs to paste into Canva, in one shot */}
            <Paper
              sx={{
                borderRadius: "14px",
                p: { xs: 2, sm: 2.5 },
                boxShadow: 0,
                bgcolor: alpha(colors.primary[400], isDark ? 0.14 : 0.08),
                border: `1px solid ${alpha(colors.primary[400], 0.3)}`,
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Building this in Canva?</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Copies the loved one's details, obituary, order of service, hymns, pallbearers, and acknowledgement as one formatted block.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<CopyIcon />}
                  onClick={() => void copyText(buildCanvaText(order), "Full program content")}
                  sx={{
                    bgcolor: colors.primary[400], color: brandTextColor, borderRadius: "10px",
                    textTransform: "none", fontWeight: 600, boxShadow: 0, whiteSpace: "nowrap",
                    "&:hover": { bgcolor: colors.primary[500] },
                  }}
                >
                  Copy all content
                </Button>
              </Stack>
            </Paper>

            {/* Program info card */}
            <Paper
              sx={{
                borderRadius: "14px",
                p: { xs: 2, sm: 2.5 },
                boxShadow: 0,
                bgcolor: colors.primary[900],
              }}
            >
              <CardHeader
                title="Loved one & service details"
                copyValue={[
                  order.deceasedName,
                  order.programSubtitle,
                  [order.birthDate, order.passingDate].filter(Boolean).join("  -  "),
                  order.venue && `Venue: ${order.venue}`,
                  order.serviceTime && `Service time: ${order.serviceTime}`,
                ].filter(Boolean).join("\n")}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 3,
                  rowGap: 2.5,
                }}
              >
                <IconInfoRow
                  icon={<PersonIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Loved one"
                  value={order.deceasedName}
                />
                <IconInfoRow
                  icon={<ArticleIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Selected template"
                  value={order.template}
                />
                <IconInfoRow
                  icon={<FavoriteIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Dates (sunrise - sunset)"
                  value={`${formatDate(order.birthDate)} - ${formatDate(order.passingDate)}`}
                />
                <IconInfoRow
                  icon={<CalendarIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Service date"
                  value={formatDate(order.serviceDate)}
                />
                <IconInfoRow
                  icon={<DeliveryIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Venue"
                  value={order.venue || "Not provided"}
                />
                <IconInfoRow
                  icon={<PrintIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Print specs"
                  value={`${order.copies} copies · ${order.pages} pages · ${order.paper} paper`}
                />
                <IconInfoRow
                  icon={<DeliveryIcon sx={{ fontSize: 18, color: colors.primary[400] }} />}
                  label="Delivery / Collection"
                  value={order.deliveryAddress || "Not provided"}
                />
              </Box>

              {order.programSubtitle && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" mt={2}>
                  "{order.programSubtitle}"
                </Typography>
              )}
            </Paper>

            {/* Cover photo / design source */}
            {(order.coverPhoto || order.ownFile) && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <SectionLabel>Design source</SectionLabel>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {order.coverPhoto && (
                    <Stack spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={order.coverPhoto}
                        alt={`${order.deceasedName} portrait`}
                        sx={{ width: 100, height: 100, borderRadius: "12px", objectFit: "cover", border: `1px solid ${theme.palette.divider}` }}
                      />
                      <Button size="small" component="a" href={order.coverPhoto} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", fontSize: 11 }}>
                        Open full size
                      </Button>
                    </Stack>
                  )}
                  {order.ownFile && (
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: "10px", bgcolor: isDark ? alpha(theme.palette.common.white, 0.03) : theme.palette.background.default }}>
                        <PdfIcon sx={{ color: colors.primary[400] }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>Customer supplied their own PDF</Typography>
                          <Button size="small" component="a" href={order.ownFile} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", fontSize: 11, p: 0 }}>
                            Open file
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Obituary */}
            {order.obituary && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <CardHeader title="Obituary" copyValue={order.obituary} />
                <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                  {order.obituary}
                </Typography>
              </Paper>
            )}

            {/* Order of service */}
            {order.sections.length > 0 && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <CardHeader
                  title="Order of service"
                  copyValue={order.sections.map((s, i) => `${i + 1}. ${s.title}${s.detail ? ` - ${s.detail}` : ""}`).join("\n")}
                />
                <Stack spacing={1.5}>
                  {order.sections.map((section, index) => (
                    <Stack key={`${section.title}-${index}`} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{
                        width: 22, height: 22, borderRadius: "6px", bgcolor: alpha(colors.primary[400], 0.12),
                        color: colors.primary[400], display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, mt: 0.2,
                      }}>
                        {index + 1}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{section.title}</Typography>
                        {section.detail && (
                          <Typography variant="caption" color="text.secondary">{section.detail}</Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Hymns */}
            {order.hymns.length > 0 && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <CardHeader
                  title="Hymns & lyrics"
                  copyValue={order.hymns.map((h) => `${h.title}${h.performer ? ` (${h.performer})` : ""}\n${h.lyrics}`).join("\n\n")}
                />
                <Stack spacing={2}>
                  {order.hymns.map((hymn, index) => (
                    <Box key={`${hymn.title}-${index}`}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MusicIcon sx={{ fontSize: 16, color: colors.primary[400] }} />
                        <Typography variant="body2" fontWeight={700}>{hymn.title}</Typography>
                        {hymn.performer && <Typography variant="caption" color="text.secondary">- {hymn.performer}</Typography>}
                      </Stack>
                      {hymn.lyrics && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", mt: 0.5, ml: 3, lineHeight: 1.7 }}>
                          {hymn.lyrics}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

          </Stack>
        </Grid>

        {/* Right: Applicant + Order Summary + Client notes + Hymns + Pallbearers + Acknowledgement */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2.5}>
            {/* Applicant card */}
            <Paper
              sx={{
                borderRadius: "14px",
                p: { xs: 2, sm: 2.5 },
                boxShadow: 0,
                bgcolor: colors.primary[900],
              }}
            >
              <SectionLabel>Applicant</SectionLabel>

              <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: colors.primary[400],
                    fontWeight: 700,
                    fontSize: "15px",
                  }}
                >
                  {getInitials(order.clientName)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: isDark ? colors.primary[100] : colors.primary[100] }}>
                    {order.clientName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.email}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75}>
                  <IconButton
                    size="small"
                    onClick={() => setMessageOpen(true)}
                    sx={{
                      bgcolor: alpha(colors.primary[400], isDark ? 0.18 : 0.1),
                      "&:hover": { bgcolor: alpha(colors.primary[400], isDark ? 0.32 : 0.18) },
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 16, color: colors.primary[400] }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    component="a"
                    href={`mailto:${order.email}`}
                    sx={{
                      bgcolor: alpha(colors.primary[400], isDark ? 0.18 : 0.1),
                      "&:hover": { bgcolor: alpha(colors.primary[400], isDark ? 0.32 : 0.18) },
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 16, color: colors.primary[400] }} />
                  </IconButton>
                </Stack>
              </Stack>

              <Box
                sx={{
                  bgcolor: isDark ? colors.primary[800] : theme.palette.background.default,
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Phone</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? colors.primary[300] : colors.primary[100] }}>
                  {order.phone}
                </Typography>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
                  Submitted {new Date(order.submittedAt).toLocaleString("en-GB")}
                </Typography>
              </Box>
            </Paper>

            {/* Order summary -- print cost, add-ons, and the total all in one place */}
            <Paper
              sx={{
                borderRadius: "14px",
                p: { xs: 2, sm: 2.5 },
                boxShadow: 0,
                bgcolor: colors.primary[900],
              }}
            >
              <SectionLabel>Order summary</SectionLabel>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Print ({order.copies} copies · {order.pages} pages · {order.paper})
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {order.printSubtotal == null ? "To confirm" : `$${order.printSubtotal.toFixed(2)}`}
                  </Typography>
                </Stack>
                {order.addOns.map((addOn) => (
                  <Stack key={addOn.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{addOn.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>${addOn.price.toFixed(2)}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Divider
                sx={{
                  my: 1.75,
                  borderColor: isDark
                    ? alpha(theme.palette.common.white, 0.06)
                    : theme.palette.divider,
                }}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 600 }}>
                  Order total
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: colors.primary[400],
                    letterSpacing: "-0.5px",
                  }}
                >
                  {order.total == null ? "To confirm" : `$${order.total.toFixed(2)}`}
                </Typography>
              </Stack>
            </Paper>

            {/* Client notes */}
            {order.notes && (
              <Paper
                sx={{
                  borderRadius: "14px",
                  p: { xs: 2, sm: 2.5 },
                  boxShadow: 0,
                  bgcolor: colors.primary[900],
                }}
              >
                <CardHeader title="Client notes" copyValue={order.notes} />
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <NotesIcon sx={{ fontSize: 18, color: colors.primary[400], mt: 0.2, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    {order.notes}
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Hymns -- quick reference; full lyrics are in the Hymns &
                lyrics card in the main column. */}
            {order.hymns.length > 0 && (
              <Paper
                sx={{
                  borderRadius: "14px",
                  p: { xs: 2, sm: 2.5 },
                  boxShadow: 0,
                  bgcolor: colors.primary[900],
                }}
              >
                <CardHeader
                  title="Hymns"
                  copyValue={order.hymns.map((h) => `${h.title}${h.performer ? ` (${h.performer})` : ""}`).join("\n")}
                />
                <Stack spacing={1.25}>
                  {order.hymns.map((hymn, index) => (
                    <Stack key={`${hymn.title}-${index}`} direction="row" spacing={1} alignItems="flex-start">
                      <MusicIcon sx={{ fontSize: 16, color: colors.primary[400], mt: 0.2, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{hymn.title}</Typography>
                        {hymn.performer && (
                          <Typography variant="caption" color="text.secondary">{hymn.performer}</Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Pallbearers */}
            {order.pallbearers.length > 0 && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <CardHeader title="Pallbearers" copyValue={order.pallbearers.join(", ")} />
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {order.pallbearers.map((name, index) => (
                    <UiChip key={`${name}-${index}`} label={name} status="neutral" icon={<GroupIcon sx={{ fontSize: 14 }} />} />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Acknowledgement */}
            {order.acknowledgement && (
              <Paper sx={{ borderRadius: "14px", p: { xs: 2, sm: 2.5 }, boxShadow: 0, bgcolor: colors.primary[900] }}>
                <CardHeader title="Acknowledgement" copyValue={order.acknowledgement} />
                <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                  {order.acknowledgement}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* WhatsApp message dialog */}
      <WhatsAppMessageDialog
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        clientName={order.clientName}
        clientPhone={order.phone}
        staffName={user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "Muzukuru"}
        topic={`the memorial program order ${order.orderNumber} for ${order.deceasedName}`}
        initialMessage={`Hi ${order.clientName.split(" ")[0]}, thanks for submitting the memorial program for ${order.deceasedName}. We are reviewing order ${order.orderNumber} and will be in touch about the proof and printing.`}
        templateStorageKey="whatsapp_program_order_template"
        onSendBusiness={sendWithMuzukuruBusiness}
        sendingBusiness={sendingBusiness}
      />
    </Box>
  );
};

/* ── Small helpers ─────────────────────────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.6px",
        color: "text.primary",
        mb: 2,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}

function CopyIconButton({ text, label }: { text: string; label: string }) {
  if (!text) return null;
  return (
    <IconButton
      size="small"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      onClick={() => void copyText(text, label)}
      sx={{ p: 0.5 }}
    >
      <CopyIcon sx={{ fontSize: 15 }} />
    </IconButton>
  );
}

function CardHeader({ title, copyLabel, copyValue }: { title: string; copyLabel?: string; copyValue?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
      <SectionLabel>{title}</SectionLabel>
      {copyValue ? <CopyIconButton text={copyValue} label={copyLabel ?? title} /> : null}
    </Stack>
  );
}

function IconInfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ mt: 0.2, flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.4 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

const formatDate = (value: string | null) =>
  value
    ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Not provided";

export default ProgramOrderDetailPage;



